import { z } from 'zod';
import { defineListQuery, listFilter } from './list-query.schemas';

/**
 * Service plans and intervals as data (PRD → Maintenance → Sources of work (1); gitaumoses4/chukua#162).
 * An interval set is all-or-nothing: a row carries its own km/days/hours legs or none at all, never a
 * mix with another row's, so "drop the km leg for this class" is expressible.
 */

const positiveInteger = z.number().int().positive();

const intervalFields = {
  intervalKm: positiveInteger.nullish(),
  intervalDays: positiveInteger.nullish(),
  intervalHours: positiveInteger.nullish(),
};

export interface ServiceInterval {
  intervalKm: number | null;
  intervalDays: number | null;
  intervalHours: number | null;
}

export function hasInterval(interval: Partial<ServiceInterval>): boolean {
  return interval.intervalKm != null || interval.intervalDays != null || interval.intervalHours != null;
}

const intervalOrNull = <T extends { intervalKm?: number | null; intervalDays?: number | null; intervalHours?: number | null }>(
  input: T,
): T & ServiceInterval => ({
  ...input,
  intervalKm: input.intervalKm ?? null,
  intervalDays: input.intervalDays ?? null,
  intervalHours: input.intervalHours ?? null,
});

const AT_LEAST_ONE_INTERVAL = 'Give at least one interval: km, days or hours';

/**
 * `POST /service-items`, `PATCH /service-items/:id` — the item's own default interval, used by every class line that doesn't set its own,
 * and the truck classes it applies to. `truckClassCodes` is the whole set: classes left out lose the item, and omitting the field leaves
 * the item's classes as they are.
 */
export const serviceItemInputSchema = z
  .object({
    code: z.string().trim().min(1).max(32),
    name: z.string().trim().min(1).max(120),
    truckClassCodes: z
      .array(z.string().trim().min(1).max(64))
      .max(200)
      .transform((codes) => [...new Set(codes)])
      .optional(),
    ...intervalFields,
  })
  .refine(hasInterval, { message: AT_LEAST_ONE_INTERVAL, path: ['intervalKm'] })
  .transform(intervalOrNull);
export type ServiceItemInput = z.output<typeof serviceItemInputSchema>;

/** `PUT /service-plans/:classCode/items/:itemId` — no interval at all means "use the item's own". */
export const servicePlanLineInputSchema = z.object(intervalFields).transform(intervalOrNull);
export type ServicePlanLineInput = z.output<typeof servicePlanLineInputSchema>;

/** `PUT /trucks/:truckId/service-overrides/:itemId` — either leaves the item out for this truck, or gives this truck's own intervals. */
export const truckServiceOverrideInputSchema = z
  .object({ isExcluded: z.boolean().default(false), ...intervalFields })
  .superRefine((input, ctx) => {
    if (input.isExcluded && hasInterval(input)) ctx.addIssue({ code: 'custom', message: 'An excluded item cannot have an interval', path: ['isExcluded'] });
    if (!input.isExcluded && !hasInterval(input)) ctx.addIssue({ code: 'custom', message: AT_LEAST_ONE_INTERVAL, path: ['intervalKm'] });
  })
  .transform(intervalOrNull);
export type TruckServiceOverrideInput = z.output<typeof truckServiceOverrideInputSchema>;

const calendarDate = z.iso.date();

/** `POST /trucks/:truckId/service-items/:itemId/last-done` — the initial values entered at setup, and every later correction. */
export const recordLastDoneInputSchema = z
  .object({
    odometerKm: z.number().int().min(0).nullish(),
    doneOn: calendarDate.nullish(),
    engineHours: z.number().min(0).max(9999999.9).multipleOf(0.1).nullish(),
  })
  .refine((input) => input.odometerKm != null || input.doneOn != null || input.engineHours != null, {
    message: 'Give at least one of odometer, date or engine hours',
    path: ['odometerKm'],
  })
  .transform((input) => ({ odometerKm: input.odometerKm ?? null, doneOn: input.doneOn ?? null, engineHours: input.engineHours ?? null }));
export type RecordLastDoneInput = z.output<typeof recordLastDoneInputSchema>;

export const SERVICE_ITEM_STATUSES = ['active', 'inactive'] as const;

/** `GET /service-items` — search matches code or name. */
export const serviceItemListQuerySchema = defineListQuery({
  sortFields: ['name', 'code'],
  defaultSort: { sort: 'name', order: 'asc' },
  filters: { status: listFilter(z.enum(SERVICE_ITEM_STATUSES)) },
});
export type ServiceItemListQuery = z.infer<typeof serviceItemListQuerySchema>;

export const SERVICE_ITEM_SOURCES = ['class', 'override'] as const;
export type ServiceItemSource = (typeof SERVICE_ITEM_SOURCES)[number];

/** `GET /trucks/:truckId/service-items` — the resolved plan; `source` keeps items that come from the class or from a truck override. */
export const truckServiceItemListQuerySchema = defineListQuery({
  sortFields: ['name', 'code'],
  defaultSort: { sort: 'name', order: 'asc' },
  filters: { source: listFilter(z.enum(SERVICE_ITEM_SOURCES)) },
});
export type TruckServiceItemListQuery = z.infer<typeof truckServiceItemListQuerySchema>;

/** `GET /trucks/:truckId/service-records` — every service entered for the truck, newest first; search matches the item's code or name. */
export const serviceRecordListQuerySchema = defineListQuery({
  sortFields: ['recordedAt', 'doneOn', 'odometerKm', 'name'],
  defaultSort: { sort: 'recordedAt', order: 'desc' },
  filters: { serviceItem: listFilter(z.uuid()) },
});
export type ServiceRecordListQuery = z.infer<typeof serviceRecordListQuerySchema>;

export interface ServiceItemView extends ServiceInterval {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  /** The truck classes whose plan includes this item, by code. */
  truckClassCodes: string[];
}

export interface ServicePlanLineView extends ServiceInterval {
  serviceItemId: string;
  code: string;
  name: string;
  /** True when the line carries no interval of its own and the item's applies. */
  usesItemInterval: boolean;
}

export interface LastDoneView {
  id: string;
  odometerKm: number | null;
  doneOn: string | null;
  engineHours: number | null;
  recordedBy: string | null;
  recordedAt: string;
}

export interface ServiceRecordView extends LastDoneView {
  serviceItemId: string;
  code: string;
  name: string;
}

export interface TruckServiceItemView extends ServiceInterval {
  serviceItemId: string;
  code: string;
  name: string;
  source: ServiceItemSource;
  /** `null` until a last-done value is recorded for this truck and item. */
  lastDone: LastDoneView | null;
}

export interface TruckServiceOverrideView extends ServiceInterval {
  serviceItemId: string;
  code: string;
  name: string;
  isExcluded: boolean;
}
